import type * as NonEmptyArray from 'web-app/util/non-empty-array';
import type Institution from 'web-app/react/entities/institutions/model';

import {
    type ApiAccessTokenSettingsFragment,
    type GetFinanceBehaviorsQuery,
    type GetInstitutionsQuery,
} from './__generated__/queries.api-types';
import {type GetPricingGroupsWithBehaviorsQuery} from '../plans/manage-pricing-groups/__generated__/queries.api-types';

/*
|------------------------------------------------------------------------------
| Types
|------------------------------------------------------------------------------
*/

/**
 * A setting item that has child items. In the UI this means that
 * it should show up as a foldable tab, showing the children when unfolded.
 */
export interface SettingsItemWithChildItems {
    type: 'settings-item-with-child-items';
    id: string;
    title: string;
    childItems: NonEmptyArray.NonEmptyArray<SettingsItemWithoutChildItems | SettingsItemWithoutChildItemsAndContent>;
    icon?: React.ReactNode;
}

/**
 * A settings item without child items. When this item is selected
 * in the UI the 'detailComponent' should be shown.
 *
 * Use `searchKeywords` to expose searchable terms to the filter, which
 * are otherwise only present in the content.
 */
export interface SettingsItemWithoutChildItems {
    type: 'settings-item-without-child-items';
    id: string;
    title: string;
    searchKeywords: string[];
    detailComponent: React.ReactNode;
    icon?: React.ReactNode;
}

/**
 * A settings item without child items and without content. When this
 * item is selected in the UI the page should redirect to 'route'.
 */
export interface SettingsItemWithoutChildItemsAndContent {
    type: 'settings-item-without-child-items-and-content';
    id: string;
    title: string;
    icon?: React.ReactNode;
    route: string;
}

export type ISettingsItem =
    | SettingsItemWithChildItems
    | SettingsItemWithoutChildItems
    | SettingsItemWithoutChildItemsAndContent;

export interface ISettingsSection {
    id: string;
    title: string;
    items: NonEmptyArray.NonEmptyArray<ISettingsItem>;
}

export type InstitutionFromGQL = GetInstitutionsQuery['institutions'][0];
export type ApiAccessTokenSettings = ApiAccessTokenSettingsFragment;
export type InstitutionFromREST = ReturnType<typeof Institution.fromAPI>;

/**
 * IMPORTANT: These Id's are also used for product tours on Intercom.
 *            It is, therefore, quite important that they are not altered
 *            without carefully making sure there is not a live product tour
 *            relying on them on Intercom.
 */

export enum TabIds {
    // --- Site settings ---
    SITE_SETTINGS = 'SITE_SETTINGS',
    GENERAL = 'GENERAL',
    GENERAL_INFORMATION = 'GENERAL_INFORMATION',
    OPENING_HOURS = 'OPENING_HOURS',
    CLOSING_DAYS = 'CLOSING_DAYS',
    PRIVACY = 'PRIVACY',
    ROOMS = 'ROOMS',
    ROOM_OVERVIEW = 'ROOM_OVERVIEW',
    ROOM_INFO = 'ROOM_INFO',
    ROOM_STATUSES = 'ROOM_STATUSES',
    CHILDREN = 'CHILDREN',
    ROOM_MOVE_RULES = 'ROOM_MOVE_RULES',
    AGE_GROUPS = 'AGE_GROUPS',
    PARENTS_ROLES_PERMISSIONS = 'PARENTS_ROLES_PERMISSIONS',
    PARENTS = 'PARENTS',
    PARENTS_LOGIN_OPTIONS = 'PARENTS_LOGIN_OPTIONS',
    PARENTS_PERMISSIONS = 'PARENTS_PERMISSIONS',
    STAFF_ROLES_PERMISSIONS = 'STAFF_ROLES_PERMISSIONS',
    STAFF_QUALIFICATIONS = 'STAFF_QUALIFICATIONS',
    STAFF = 'STAFF',
    INTEGRATIONS = 'INTEGRATIONS',
    INTEGRATIONS_PARAGON = 'INTEGRATIONS_PARAGON',
    WATCH_ME_GROW = 'WATCH_ME_GROW',
    SECURITY = 'SECURITY',
    AUTO_LOGOUT = 'AUTO_LOGOUT',
    MANAGE_API_TOKENS = 'MANAGE_API_TOKENS',
    WEBHOOKS = 'WEBHOOKS',
    PUBLIC_FUNDING = 'PUBLIC_FUNDING',
    HEALTH_SCREENING_STATEMENTS = 'HEALTH_SCREENING_STATEMENTS',

    // --- Feature settings ---
    FEATURE_SETTINGS = 'FEATURE_SETTINGS',
    NEWS_FEED = 'NEWS_FEED',
    COMMUNICATION = 'COMMUNICATION',
    MESSAGING = 'MESSAGING',
    FINANCES = 'FINANCES',
    INVOICING = 'INVOICING',
    DISCOUNTS = 'DISCOUNTS',
    INVOICING_LAYOUT = 'INVOICING_LAYOUT',
    PRICING_GROUPS = 'PRICING_GROUPS',
    PRICING_MOVE_RULES = 'PRICING_MOVE_RULES',
    SEPA_INTEGRATION = 'SEPA_INTEGRATION',
    SESSION_PRODUCT_AND_PACKAGE = 'SESSION_PRODUCT_AND_PACKAGE', // Still just a link to the old setup page.
    PARENT_BOOKINGS = 'PARENT_BOOKINGS', // Still just a link to the old setup page.
    ACCOUNTING = 'ACCOUNTING', // Still just a link to the old setup page.
    XERO = 'XERO',
    AUTOMATIC_INVOICING_CONFIG = 'AUTOMATIC_INVOICING_CONFIG',
    KITABULI = 'KITABULI',
    FUNDING_SETTINGS = 'FUNDING_SETTINGS', // Simple link to separate route.
    IN_APP_PAYMENTS = 'IN_APP_PAYMENTS', // Simple link to separate route.
    SIDEKICK = 'SIDEKICK',
    DATEV = 'DATEV',
    SMS = 'SMS',

    // --- App settings ---
    APP_SETTINGS = 'APP_SETTINGS',
    CHILD_DEVELOPMENT = 'CHILD_DEVELOPMENT',
    MEALS = 'MEALS',
    MEAL_PLANS = 'MEAL_PLANS',
    MEAL_BOOKING = 'MEAL_BOOKING',
    SAFEGUARDING = 'SAFEGUARDING',
    BOOKINGS = 'BOOKINGS',
    FRAMEWORKS = 'FRAMEWORKS',
    PARENT_OBSERVATIONS = 'PARENT_OBSERVATIONS',
    ASSESSMENT_CONFIGURATION = 'ASSESSMENT_CONFIGURATION',
    CHILD_DEVELOPMENT_SETUP = 'CHILD_DEVELOPMENT_SETUP',
    CHILD_DEVELOPMENT_REFERENCE_MATERIAL = 'CHILD_DEVELOPMENT_REFERENCE_MATERIAL',
    CHILD_DEVELOPMENT_LEARNING_FRAMEWORK = 'CHILD_DEVELOPMENT_LEARNING_FRAMEWORK',
    STAFF_LEAVE = 'STAFF_LEAVE',
    STAFF_HOURS = 'STAFF_HOURS',
    SIGN_IN_OUT = 'SIGN_IN_OUT',
    HOMEPAGE = 'HOMEPAGE',
    TRY_UPCOMING_FEATURES = 'TRY_UPCOMING_FEATURES',
    MY_ACCOUNT = 'MY_ACCOUNT',
}

export type FinanceIntegrationBehaviors = GetFinanceBehaviorsQuery['finance']['integration']['info']['behaviors'];

export type PricingGroupBehaviors = GetPricingGroupsWithBehaviorsQuery['finance']['pricingGroups']['behaviors'];

export enum ReferenceMaterials {
    ELG = 'Early Learning Goals',
    BT5M = 'Birth to 5 Matters',
    COEL_BT5 = 'Characteristics of Effective Learning (Bt5)',
    DM = 'Development Matters',
    COEL_DM = 'Characteristics of Effective Learning (DM)',
    OC_DM = 'Observation Checkpoints',
    CFE = 'Curriculum for Excellence (Scotland)',
    DP = 'Developmental Pathways (Wales)',
    RTA = 'Realising the Ambition (Scotland)',
}

export enum LearningFrameworks {
    EYFS = 'EYFS',
    COEL = 'Characteristics of Effective Learning',
    GIRFEC = 'GIRFEC (Scotland)',
    CFE = 'Curriculum for Excellence (Scotland)',
    DP = 'Developmental Pathways (Wales)',
    RTA = 'Realising the Ambition (Scotland)',
}
